import { Box, Flex } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSwitchInput } from 'components/common/form/switch';
import { TableProvider } from 'components/common/table/context';
import { PitchPerfControls } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/controls';
import { PitchPerfGroupTable } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/group-table';
import { PitchPerfMetrics } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/metrics';
import { PitchPerfPlots } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots';
import { PitchPerfShotTableHoC } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/shot-table';
import usePitchPerformanceStore, {
  ENABLE_COMPARISON_PLOTS,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

const COMPONENT_NAME = 'PitchPerformance';

enum Period {
  Current = 'Current',
  Previous = 'Previous',
}

interface IProps {
  machine: IMachine;
}

export const PitchPerformanceHoC = (props: IProps) => {
  const store = usePitchPerformanceStore(
    useShallow((state) => ({
      allGroups: state.allGroups,
    }))
  );

  // we need to wrap everything in a table and checked provider
  // so that we can use the table to control the charts + metrics

  return (
    <ErrorBoundary componentName="PitchPerformanceHoC">
      <TableProvider>
        <CheckedProvider data={store.allGroups}>
          <PitchPerformance {...props} />
        </CheckedProvider>
      </TableProvider>
    </ErrorBoundary>
  );
};

const PitchPerformance = (props: IProps) => {
  const {
    filters,
    allGroups,
    prevGroups,
    firstGroups,
    machineID,
    loading,
    selectedPitchId,
    initialize,
    generate,
    reset,
    setSelectedPitchId,
    setFirstGroups,
    fetchStatsPitches,
  } = usePitchPerformanceStore(
    useShallow((state) => ({
      loading: state.loading,
      filters: state.filters,

      allGroups: state.allGroups,
      prevGroups: state.prevGroups,
      firstGroups: state.firstGroups,

      machineID: state.machineID,
      selectedPitchId: state.selectedPitchId,

      initialize: state.initialize,
      generate: state.generate,
      reset: state.reset,

      setSelectedPitchId: state.setSelectedPitchId,
      setFirstGroups: state.setFirstGroups,

      fetchStatsPitches: state.fetchStatsPitches,
    }))
  );

  const { tally, setChecked, resetChecked, getAllChecked, getChecked } =
    useContext(CheckedContext);

  // array of pitch IDs checked-off at time of drill down
  const checkedGroups = useRef<string[]>([]);

  // for what is plotted
  const [period, setPeriod] = useState(Period.Current);

  useEffect(() => {
    setPeriod((prev) => {
      if (prev === Period.Previous && !filters.comparing) {
        return Period.Current;
      }

      return prev;
    });
  }, [filters.comparing, setPeriod]);

  const visibleGroups = useMemo(() => {
    if (tally.checked === 0) {
      // nothing checked => everything is visible
      return allGroups;
    }

    return allGroups.filter((g) => getChecked(g._id));
  }, [allGroups, tally]);

  useEffect(() => {
    setFirstGroups(visibleGroups);
  }, [visibleGroups]);

  useEffect(() => {
    fetchStatsPitches(firstGroups, true);
  }, [firstGroups]);

  useEffect(() => {}, []);

  const visibleGroupsPrev = useMemo(() => {
    if (tally.checked === 0) {
      // nothing checked => everything is visible
      return prevGroups;
    }

    return prevGroups.filter((g) => getChecked(g._id));
  }, [prevGroups, tally.key, tally.checked]);

  useEffect(() => {
    initialize(props.machine.machineID, props.machine.ball_type);

    return () => reset();
  }, [props.machine.ball_type, props.machine.machineID, initialize, reset]);

  // re-generate report whenever filters change
  useEffect(() => {
    if (machineID) {
      resetChecked();
      setSelectedPitchId('');
      generate();
    }
  }, [machineID, filters, generate]);

  const onDrillDown = (id: string) => {
    // keep track of what groups were checked off before drilling down so we can restore them after drilling back up
    checkedGroups.current = getAllChecked();

    setSelectedPitchId(id);

    // setting checked here will show only the drilled down pitch in the break/plate plots
    resetChecked();
    setChecked([id], true);
  };

  const onDrillup = () => {
    // restore the groups that were checked off before drilling down
    resetChecked();
    setChecked(checkedGroups.current, true);
  };

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Flex gap="5" direction="column" mt="-1">
        <PitchPerfControls machine={props.machine} />

        <PitchPerfMetrics
          currGroups={visibleGroups}
          prevGroups={visibleGroupsPrev}
          loading={loading}
        />

        {ENABLE_COMPARISON_PLOTS && filters.comparing && (
          <CommonSwitchInput
            id="TempTogglePlotPeriod"
            label="Plot Previous"
            checked={period === Period.Previous}
            onCheckedChange={(v) => {
              setPeriod(v ? Period.Previous : Period.Current);
            }}
          />
        )}

        {period === Period.Current && (
          <PitchPerfPlots pitches={visibleGroups} />
        )}

        {period === Period.Previous && (
          <CommonCallout text="Previous comparison has not been configured yet." />
        )}

        {/* keep tables in the DOM to persist any internal state they're maintaining (e.g. search queries) */}
        <Box className={!selectedPitchId ? 'hidden' : ''}>
          <PitchPerfShotTableHoC onDrillUp={onDrillup} />
        </Box>

        <Box className={selectedPitchId ? 'hidden' : ''}>
          <PitchPerfGroupTable
            machine={props.machine}
            onDrillDown={onDrillDown}
          />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
